import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import { Popover, Input, Tag, Icon, Button } from 'antd';
import classname from 'classname';
import industryDataSource from './data.json';
import './index.scss';

const DEFAULT_MAX_SELECTION = 10;
const SEARCH_DEBOUNCE = 250;

const composeSubIndustryValue = (parentCode: string, subCode: string) =>
    `${parentCode}_${subCode}`;

const composeCategoryValue = (categoryCode: string) => categoryCode;

export interface IIndustryRecord {
    code: string;
    level: number;
    name: string;
    sub?: Array<{
        code: string;
        level: number;
        name: string;
    }>;
}

export interface IIndustryCategory {
    code: string;
    name: string;
    selectCode: string;
    children: IIndustrySubIndustry[];
}

export interface IIndustrySubIndustry {
    code: string;
    rawCode: string;
    name: string;
    parentCode: string;
    parentName: string;
}

export interface IIndustryOption {
    code: string;
    name: string;
    level: number;
    parentCode?: string;
    parentName?: string;
    rawCode?: string;
}

export interface IIndustrySelectorProps {
    value?: string[];
    defaultValue?: string[];
    onChange?: (codes: string[], options: IIndustryOption[]) => void;
    onConfirm?: (codes: string[], options: IIndustryOption[]) => void;
    onReset?: () => void;
    onReachMax?: (max: number) => void;
    maxSelection?: number;
    dataSource?: IIndustryRecord[];
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    placeholder?: string;
    searchPlaceholder?: string;
    confirmText?: string;
    resetText?: string;
    footerTip?: string;
    showFooter?: boolean;
    maxTagCount?: number;
    overlayClassName?: string;
}

const normalizeCategories = (data: IIndustryRecord[]): IIndustryCategory[] =>
    data.map(item => ({
        code: item.code,
        name: item.name,
        selectCode: composeCategoryValue(item.code),
        children:
            item.sub?.map(sub => ({
                code: composeSubIndustryValue(item.code, sub.code),
                rawCode: sub.code,
                name: sub.name,
                parentCode: item.code,
                parentName: item.name,
            })) ?? [],
    }));

const buildOptionIndex = (categories: IIndustryCategory[]) => {
    const optionMap: Record<string, IIndustryOption> = {};
    const flatOptions: IIndustryOption[] = [];
    const rawCodeMap: Record<string, string> = {};
    categories.forEach(category => {
        const categoryOption: IIndustryOption = {
            code: category.selectCode,
            name: category.name,
            level: 1,
            rawCode: category.code,
        };
        optionMap[category.selectCode] = categoryOption;
        flatOptions.push(categoryOption);
        rawCodeMap[category.code] = category.selectCode;
        rawCodeMap[category.selectCode] = category.selectCode;
        category.children.forEach(child => {
            const childOption: IIndustryOption = {
                code: child.code,
                rawCode: child.rawCode,
                name: child.name,
                level: 2,
                parentCode: child.parentCode,
                parentName: child.parentName,
            };
            optionMap[child.code] = childOption;
            flatOptions.push(childOption);
            rawCodeMap[child.rawCode] = child.code;
            rawCodeMap[child.code] = child.code;
        });
    });
    return { optionMap, flatOptions, rawCodeMap };
};

const normalizeIndustryCodes = (
    codes: string[] | undefined,
    optionMap: Record<string, IIndustryOption>,
    rawCodeMap: Record<string, string>,
) => {
    if (!Array.isArray(codes) || codes.length === 0) {
        return [];
    }
    const result: string[] = [];
    codes.forEach(origin => {
        const trimmed = typeof origin === 'string' ? origin.trim() : '';
        if (!trimmed) {
            return;
        }
        const normalized =
            optionMap[trimmed]?.code ?? rawCodeMap[trimmed] ?? trimmed;
        const finalCode =
            optionMap[normalized]?.code ?? optionMap[trimmed]?.code;
        if (!finalCode) {
            return;
        }
        if (!result.includes(finalCode)) {
            result.push(finalCode);
        }
    });
    return result;
};

const isShallowEqual = (a: string[], b: string[]) => {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
};

const filterOptions = (options: IIndustryOption[], keyword: string) => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) {
        return [];
    }
    return options.filter(option => {
        if (option.name.toLowerCase().includes(trimmed)) {
            return true;
        }
        if (option.parentName) {
            return option.parentName.toLowerCase().includes(trimmed);
        }
        return false;
    });
};

export const IndustrySelector: React.FC<IIndustrySelectorProps> = ({
    value,
    defaultValue = [],
    onChange,
    onConfirm,
    onReset,
    onReachMax,
    maxSelection = DEFAULT_MAX_SELECTION,
    dataSource = industryDataSource as IIndustryRecord[],
    disabled = false,
    className,
    style,
    placeholder = '请选择行业方向',
    searchPlaceholder = '搜索行业方向',
    confirmText = '确定',
    resetText = '重置',
    footerTip,
    showFooter = true,
    maxTagCount,
    overlayClassName,
}) => {
    const categories = useMemo(() => normalizeCategories(dataSource), [
        dataSource,
    ]);
    const { optionMap, flatOptions, rawCodeMap } = useMemo(
        () => buildOptionIndex(categories),
        [categories],
    );

    const isControlled = typeof value !== 'undefined';
    const [innerValue, setInnerValue] = useState<string[]>(() =>
        normalizeIndustryCodes(defaultValue, optionMap, rawCodeMap),
    );
    const mergedValue = (isControlled ? value : innerValue) || [];
    const normalizedMergedValue = useMemo(
        () => normalizeIndustryCodes(mergedValue, optionMap, rawCodeMap),
        [mergedValue, optionMap, rawCodeMap],
    );

    const [visible, setVisible] = useState(false);
    const [activeCategoryCode, setActiveCategoryCode] = useState<string>(
        categories.length ? categories[0].code : '',
    );
    const [searchValue, setSearchValue] = useState('');
    const [keyword, setKeyword] = useState('');
    const searchInputRef = useRef<Input | null>(null);
    const debounceTimerRef = useRef<number | null>(null);

    useEffect(() => {
        if (!categories.length) {
            setActiveCategoryCode('');
            return;
        }
        if (
            activeCategoryCode &&
            categories.some(item => item.code === activeCategoryCode)
        ) {
            return;
        }
        setActiveCategoryCode(categories[0].code);
    }, [categories, activeCategoryCode]);

    useEffect(() => {
        if (isControlled) {
            return;
        }
        const normalizedDefault = normalizeIndustryCodes(
            defaultValue,
            optionMap,
            rawCodeMap,
        );
        if (!isShallowEqual(innerValue, normalizedDefault)) {
            setInnerValue(normalizedDefault);
        }
    }, [defaultValue, isControlled, optionMap, rawCodeMap, innerValue]);

    useEffect(() => {
        if (isControlled) {
            return;
        }
        if (!isShallowEqual(innerValue, normalizedMergedValue)) {
            setInnerValue(normalizedMergedValue);
        }
    }, [isControlled, innerValue, normalizedMergedValue]);

    const selectedSet = useMemo(
        () => new Set(normalizedMergedValue),
        [normalizedMergedValue],
    );

    const triggerChange = useCallback(
        (codes: string[]) => {
            const normalizedCodes = normalizeIndustryCodes(
                codes,
                optionMap,
                rawCodeMap,
            );
            if (!isControlled) {
                setInnerValue(normalizedCodes);
            }
            if (onChange) {
                const options = normalizedCodes
                    .map(code => optionMap[code])
                    .filter((item): item is IIndustryOption => !!item);
                onChange(normalizedCodes, options);
            }
        },
        [isControlled, onChange, optionMap, rawCodeMap],
    );

    const handleReachMax = useCallback(() => {
        if (onReachMax) {
            onReachMax(maxSelection);
        }
    }, [maxSelection, onReachMax]);

    const toggleCategory = useCallback(
        (category: IIndustryCategory) => {
            if (disabled) {
                return;
            }
            const categoryValue = category.selectCode;
            const nextSet = new Set(selectedSet);
            const isSelected = nextSet.has(categoryValue);
            if (isSelected) {
                nextSet.delete(categoryValue);
            } else {
                const newCount = nextSet.size + 1;
                if (maxSelection > 0 && newCount > maxSelection) {
                    handleReachMax();
                    return;
                }
                category.children.forEach(child => nextSet.delete(child.code));
                nextSet.add(categoryValue);
            }
            triggerChange(Array.from(nextSet));
        },
        [disabled, selectedSet, maxSelection, handleReachMax, triggerChange],
    );

    const toggleSubIndustry = useCallback(
        (category: IIndustryCategory, sub: IIndustrySubIndustry) => {
            if (disabled) {
                return;
            }
            const nextSet = new Set(selectedSet);
            const categoryValue = category.selectCode;
            if (nextSet.has(categoryValue)) {
                nextSet.delete(categoryValue);
            }
            if (nextSet.has(sub.code)) {
                nextSet.delete(sub.code);
            } else {
                const newCount = nextSet.size + 1;
                if (maxSelection > 0 && newCount > maxSelection) {
                    handleReachMax();
                    return;
                }
                nextSet.add(sub.code);
            }
            triggerChange(Array.from(nextSet));
        },
        [disabled, selectedSet, maxSelection, handleReachMax, triggerChange],
    );

    const handleRemove = useCallback(
        (code: string) => {
            if (disabled) {
                return;
            }
            const nextSet = new Set(selectedSet);
            nextSet.delete(code);
            triggerChange(Array.from(nextSet));
        },
        [disabled, selectedSet, triggerChange],
    );

    const handleReset = useCallback(() => {
        if (disabled) {
            return;
        }
        triggerChange([]);
        if (onReset) {
            onReset();
        }
    }, [disabled, triggerChange, onReset]);

    const handleConfirm = useCallback(() => {
        if (disabled) {
            return;
        }
        if (onConfirm) {
            const options = normalizedMergedValue
                .map(code => optionMap[code])
                .filter((item): item is IIndustryOption => !!item);
            onConfirm(normalizedMergedValue, options);
        }
        setVisible(false);
    }, [disabled, onConfirm, normalizedMergedValue, optionMap]);

    const selectedOptions = useMemo(
        () =>
            normalizedMergedValue
                .map(code => optionMap[code])
                .filter((item): item is IIndustryOption => !!item),
        [normalizedMergedValue, optionMap],
    );

    const searchResults = useMemo(() => filterOptions(flatOptions, keyword), [
        flatOptions,
        keyword,
    ]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        searchInputRef.current?.focus();
    }, [visible]);

    useEffect(() => {
        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = window.setTimeout(() => {
            setKeyword(prev => {
                if (prev === searchValue) {
                    return prev;
                }
                return searchValue;
            });
        }, SEARCH_DEBOUNCE);
        return () => {
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchValue]);

    useEffect(() => {
        if (keyword.trim()) {
            return;
        }
        setActiveCategoryCode(prev => {
            if (prev) {
                return prev;
            }
            return categories.length ? categories[0].code : '';
        });
    }, [keyword, categories]);

    const selectedCount = normalizedMergedValue.length;
    const tipText = footerTip || `请选择行业方向，最多可选择${maxSelection}个`;

    const renderCategoryList = () => (
        <div className="crm-industry-selector-categories">
            {categories.map(category => {
                const isActive = category.code === activeCategoryCode;
                const isSelected = selectedSet.has(category.selectCode);
                const selectedChildren = category.children.filter(child =>
                    selectedSet.has(child.code),
                );
                const hasPartial = !isSelected && selectedChildren.length > 0;
                return (
                    <div
                        key={category.code}
                        className={classname('crm-industry-selector-category', {
                            active: isActive,
                            selected: isSelected,
                            partial: hasPartial,
                            disabled,
                        })}
                        onClick={() => {
                            if (!disabled) {
                                setActiveCategoryCode(category.code);
                            }
                        }}
                    >
                        <span className="name">{category.name}</span>
                        <span className="status">
                            {isSelected ? (
                                <Icon type="check" />
                            ) : hasPartial ? (
                                <Icon type="minus" />
                            ) : (
                                <Icon type="right" />
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    const renderPanel = () => {
        if (keyword.trim()) {
            return (
                <div className="crm-industry-selector-search-result">
                    {searchResults.length === 0 ? (
                        <div className="crm-industry-selector-empty">
                            未找到“{searchValue}”相关行业
                        </div>
                    ) : (
                        searchResults.map(option => {
                            const isSelected = selectedSet.has(option.code);
                            return (
                                <div
                                    key={option.code}
                                    className={classname(
                                        'crm-industry-selector-item',
                                        {
                                            selected: isSelected,
                                            disabled,
                                        },
                                    )}
                                    onClick={() => {
                                        if (option.level === 1) {
                                            const category = categories.find(
                                                item =>
                                                    item.selectCode ===
                                                    option.code,
                                            );
                                            if (category) {
                                                toggleCategory(category);
                                                setActiveCategoryCode(
                                                    category.code,
                                                );
                                            }
                                        } else if (option.parentCode) {
                                            const category = categories.find(
                                                item =>
                                                    item.code ===
                                                    option.parentCode,
                                            );
                                            const sub = category?.children.find(
                                                item =>
                                                    item.code === option.code,
                                            );
                                            if (category && sub) {
                                                toggleSubIndustry(
                                                    category,
                                                    sub,
                                                );
                                                setActiveCategoryCode(
                                                    category.code,
                                                );
                                            }
                                        }
                                    }}
                                >
                                    <span className="text">
                                        {option.level === 1
                                            ? option.name
                                            : `${option.parentName ?? ''} / ${
                                                  option.name
                                              }`}
                                    </span>
                                    {isSelected ? <Icon type="check" /> : null}
                                </div>
                            );
                        })
                    )}
                </div>
            );
        }

        const activeCategory = categories.find(
            item => item.code === activeCategoryCode,
        );
        if (!activeCategory) {
            return (
                <div className="crm-industry-selector-empty">
                    请选择行业大类
                </div>
            );
        }
        const categorySelected = selectedSet.has(activeCategory.selectCode);
        const selectedChildren = activeCategory.children.filter(child =>
            selectedSet.has(child.code),
        );
        const allSelected =
            activeCategory.children.length > 0 &&
            selectedChildren.length === activeCategory.children.length;

        return (
            <div className="crm-industry-selector-panel">
                <div className="crm-industry-selector-panel-header">
                    {activeCategory.name}
                </div>
                <div className="crm-industry-selector-tags">
                    <div
                        className={classname(
                            'crm-industry-selector-tag',
                            'tag-all',
                            {
                                selected: categorySelected || allSelected,
                                disabled,
                            },
                        )}
                        onClick={() => toggleCategory(activeCategory)}
                    >
                        全部
                        {(categorySelected || allSelected) && (
                            <Icon type="check" />
                        )}
                    </div>
                    {activeCategory.children.map(child => {
                        const isSelected =
                            categorySelected || selectedSet.has(child.code);
                        return (
                            <div
                                key={child.code}
                                className={classname(
                                    'crm-industry-selector-tag',
                                    {
                                        selected: isSelected,
                                        disabled,
                                    },
                                )}
                                onClick={() =>
                                    toggleSubIndustry(activeCategory, child)
                                }
                            >
                                <span className="text">{child.name}</span>
                                {isSelected ? <Icon type="check" /> : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderSelectedTags = () => {
        if (!selectedOptions.length) {
            return (
                <span className="crm-industry-selector-selected-placeholder">
                    尚未选择行业方向
                </span>
            );
        }
        const list =
            typeof maxTagCount === 'number' && maxTagCount >= 0
                ? selectedOptions.slice(0, maxTagCount)
                : selectedOptions;

        return (
            <>
                {list.map(option => (
                    <Tag
                        key={option.code}
                        closable={!disabled}
                        onClose={(event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            handleRemove(option.code);
                        }}
                    >
                        {option.parentName
                            ? `${option.parentName} / ${option.name}`
                            : option.name}
                    </Tag>
                ))}
                {selectedOptions.length > list.length ? (
                    <span className="crm-industry-selector-selected-more">
                        +{selectedOptions.length - list.length}
                    </span>
                ) : null}
            </>
        );
    };

    const overlayContent = (
        <div
            className="crm-industry-selector-dropdown"
            onClick={event => event.stopPropagation()}
        >
            <div className="crm-industry-selector-header">
                <Input
                    ref={searchInputRef}
                    value={searchValue}
                    placeholder={searchPlaceholder}
                    onChange={event => {
                        const nextValue = event.target.value;
                        setSearchValue(nextValue);
                    }}
                    allowClear
                />
            </div>
            <div className="crm-industry-selector-body">
                {renderCategoryList()}
                <div className="crm-industry-selector-divider" />
                <div className="crm-industry-selector-content">
                    {renderPanel()}
                </div>
            </div>
            <div className="crm-industry-selector-selected">
                {renderSelectedTags()}
            </div>
            {showFooter && (
                <div className="crm-industry-selector-footer">
                    <div className="crm-industry-selector-tip">
                        {tipText}
                        <span className="count">
                            （已选 {selectedCount}
                            {maxSelection > 0 ? `/${maxSelection}` : ''}）
                        </span>
                    </div>
                    <div className="crm-industry-selector-actions">
                        <Button onClick={handleReset} disabled={disabled}>
                            {resetText}
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleConfirm}
                            disabled={disabled}
                        >
                            {confirmText}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderTriggerValue = () => {
        if (!selectedOptions.length) {
            return (
                <span className="crm-industry-selector-placeholder">
                    {placeholder}
                </span>
            );
        }
        const list =
            typeof maxTagCount === 'number' && maxTagCount >= 0
                ? selectedOptions.slice(0, maxTagCount)
                : selectedOptions;
        return (
            <>
                {list.map(option => (
                    <Tag
                        key={option.code}
                        closable={!disabled}
                        onClose={(event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            handleRemove(option.code);
                        }}
                    >
                        {option.parentName
                            ? `${option.parentName} / ${option.name}`
                            : option.name}
                    </Tag>
                ))}
                {selectedOptions.length > list.length ? (
                    <span className="crm-industry-selector-selected-more">
                        +{selectedOptions.length - list.length}
                    </span>
                ) : null}
            </>
        );
    };

    return (
        <Popover
            overlayClassName={classname(
                'crm-industry-selector-popover',
                overlayClassName,
            )}
            placement="bottomLeft"
            trigger="click"
            visible={visible && !disabled}
            onVisibleChange={next => {
                if (disabled) {
                    return;
                }
                setVisible(next);
                if (!next) {
                    setKeyword('');
                    setSearchValue('');
                }
            }}
            content={overlayContent}
        >
            <div
                className={classname(
                    'crm-industry-selector-trigger',
                    className,
                    {
                        disabled,
                        open: visible,
                        empty: selectedOptions.length === 0,
                    },
                )}
                style={style}
                onClick={() => {
                    if (!disabled) {
                        setVisible(prev => !prev);
                    }
                }}
            >
                <div className="crm-industry-selector-value">
                    {renderTriggerValue()}
                </div>
                <div className="crm-industry-selector-controls">
                    {selectedOptions.length > 0 && !disabled ? (
                        <Icon
                            type="close-circle"
                            theme="filled"
                            className="crm-industry-selector-action"
                            onClick={event => {
                                event.stopPropagation();
                                handleReset();
                            }}
                        />
                    ) : null}
                    <Icon
                        type={visible ? 'up' : 'down'}
                        className="crm-industry-selector-action arrow"
                    />
                </div>
            </div>
        </Popover>
    );
};

export default IndustrySelector;
